
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';

import { ListingActivityType } from '@customer-activity/enums/listing-activity-type.enum';
import { ListingActivityConstants } from '@listings/constants/listing-activity-constants';
import { SetListingActivitiesRequest, SetListingActivityRequest } from '@listings/models/api/set-listing-activity-request';
import { ActivitiesClickedEventParams, ActivityClickedEventParams } from '@listings/models/events/activity-clicked-event-params';
import * as listingActivityActions from '../actions/listing-activity.actions';
import { ListingsState } from '../states/listings.state';
import { GoogleAnalyticsStoreService } from '@core-layout/app/store/services/google-analytics-store.service';

@Injectable({ providedIn: 'root' })
export class ListingActivityStoreService {

    constructor(
        private readonly store: Store<ListingsState>,
        private readonly googleAnalyticsStoreService: GoogleAnalyticsStoreService,
    ) { }

    public setListingActivity(activityClickedParams: ActivityClickedEventParams, customerId: number): void {
        this.setListingsActivity(
            { listingCandidates: [activityClickedParams.listingCandidate], activity: activityClickedParams.activity },
            customerId,
        );
    }

    public setListingActivities(activitiesClickedParams: ActivitiesClickedEventParams, customerId: number): void {
        this.setListingsActivities(
            { listingCandidates: [activitiesClickedParams.listingCandidate], activities: activitiesClickedParams.activities },
            customerId,
        );
    }

    public setListingsActivity(activityRequest: SetListingActivityRequest, customerId: number): void {
        const newMatchListings = activityRequest.listingCandidates.filter(l => l.isNewMatch);
        const commonListings = activityRequest.listingCandidates.filter(l => !l.isNewMatch);

        const correctedActivityRequest: SetListingActivityRequest = {
            listingCandidates: [],
            activity: activityRequest.activity
        };

        const activityReturnToPickListRequest: SetListingActivityRequest = {
            listingCandidates: [],
            activity: ListingActivityConstants.PickListed,
            isReactionRemoved: true
        };

        if (newMatchListings.length > 0) {
            this.store.dispatch(listingActivityActions.setListingsActivity({
                request: { listingCandidates: newMatchListings, activity: activityRequest.activity },
                customerId,
            }));
        }

        commonListings.forEach(candidate => {
            if (candidate.activities.some(activity => activity.id === activityRequest.activity.id &&
                activityRequest.activity.id !== ListingActivityConstants.PickListed.id &&
                activityRequest.activity.id !== ListingActivityConstants.Shown.id)) {
                activityReturnToPickListRequest.listingCandidates.push(candidate);
            }
        });

        commonListings.forEach(candidate => {
            if (candidate.activities.length > 1 && candidate.activities.every(activity => activity.id !== activityRequest.activity.id
                || activity.id === ListingActivityConstants.Shown.id)) {
                correctedActivityRequest.listingCandidates.push(candidate);
            } else if (candidate.activities.every(activity => activity.id !== activityRequest.activity.id)) {
                correctedActivityRequest.listingCandidates.push(candidate);
            }
        });

        if (correctedActivityRequest.listingCandidates.length > 0) {
            this.store.dispatch(listingActivityActions.setListingsActivity({
                request: correctedActivityRequest,
                customerId,
            }));

            this.googleAnalyticsStoreService.addListingReactionEvent(correctedActivityRequest.activity.id);
        }

        if (activityReturnToPickListRequest.listingCandidates.length > 0) {
            this.store.dispatch(listingActivityActions.setListingsActivity({
                request: activityReturnToPickListRequest,
                customerId,
            }));
        }
    }

    public setListingsActivities(activityRequest: SetListingActivitiesRequest, customerId: number): void {
        const correctedActivityRequest: SetListingActivitiesRequest = {
            listingCandidates: [],
            activities: activityRequest.activities
        };

        const activityReturnToPickListRequest: SetListingActivityRequest = {
            listingCandidates: [],
            activity: ListingActivityConstants.PickListed,
            isReactionRemoved: true
        };

        const shownActivityRequest: SetListingActivityRequest = {
            listingCandidates: [],
            activity: ListingActivityConstants.Shown,
            isReactionRemoved: true
        };

        activityRequest.listingCandidates.forEach(candidate => {
            const isAllActivitiesMatches = candidate.activities.length > 0 && candidate.activities
                .every(candidateActivity => activityRequest.activities.some(requestActivity => candidateActivity.id === requestActivity.id));

            const isRequestShownActivity = activityRequest.activities.some(candidateActivity => candidateActivity.id === ListingActivityConstants.Shown.id);
            const isCandidatePickListedActivity = candidate.activities.some(candidateActivity => candidateActivity.id === ListingActivityConstants.PickListed.id);

            if (isRequestShownActivity && isCandidatePickListedActivity) {
                shownActivityRequest.listingCandidates.push(candidate);
            } else if (isAllActivitiesMatches && !isCandidatePickListedActivity) {
                activityReturnToPickListRequest.listingCandidates.push(candidate);
            }

            activityRequest.activities.forEach(a => {
                if (candidate.activities.length > 1 && candidate.activities.every(activity => activity.id !== a.id)) {
                    correctedActivityRequest.listingCandidates.push(candidate);
                }
                else if (candidate.activities.every(activity => activity.id !== a.id)) {
                    correctedActivityRequest.listingCandidates.push(candidate);
                }
            });
        });

        if (correctedActivityRequest.listingCandidates.length > 0 && correctedActivityRequest.activities.length === 1) {
            this.store.dispatch(listingActivityActions.setListingsActivity({
                request: { listingCandidates: activityRequest.listingCandidates, activity: activityRequest.activities[0] },
                customerId,
            }));

            this.googleAnalyticsStoreService.addListingReactionEvent(activityRequest.activities[0].id);
        }

        if (activityReturnToPickListRequest.listingCandidates.length > 0) {
            this.store.dispatch(listingActivityActions.setListingsActivity({
                request: activityReturnToPickListRequest,
                customerId,
            }));
        }

        if (shownActivityRequest.listingCandidates.length > 0) {
            this.store.dispatch(listingActivityActions.setListingsActivity({
                request: shownActivityRequest,
                customerId,
            }));
        }
    }

    public addListingSessionActivity(listingActivityType: ListingActivityType, listingCategory: { listingId: string, category: string }[]): void {
        this.store.dispatch(listingActivityActions.addListingSessionActivity({ listingActivityType, listingCategory }));
    }
}